import { useRef, useState } from "react";
import { DEFAULT_DURATION } from "../SchedulePicker/constants";
import { hasOverlappingClass, isClassDay } from "../services/timings";

/*
 * Compared as the wire sees it — the weekday/time pairs and the two scalars —
 * rather than by row identity, so removing and re-adding the same class is not
 * a change.
 */
const signature = (schedule) =>
  JSON.stringify({
    startDate: schedule?.startDate,
    duration: String(schedule?.duration),
    days: (schedule?.days ?? []).map((entry) => `${entry?.day}@${entry?.time ?? ""}`).sort(),
  });

/**
 * One schedule's editable state: start date, class length and the classes that
 * `SchedulePicker` renders. Both screens hold two of these — the batch's own and
 * a new successor's when it has its own schedule.
 *
 * Classes are keyed by their own id, not by weekday: a weekday that has two
 * classes a week is two entries (R-04-04), so adding an already-added weekday
 * adds another class. `load` seeds a saved schedule and records it as the
 * baseline `isChanged` compares against (edit's "Find mentors for this
 * schedule" stays disabled until the schedule differs, R-08-14).
 *
 * @returns {{ state: {startDate: string, duration: string,
 *                     days: {id: string, day: string, time: string|null}[]},
 *             derived: {isChanged: boolean, isComplete: boolean,
 *                       isStartOnClassDay: boolean},
 *             handlers: Object, load: (schedule: Object) => void }}
 */
export const useScheduleDraft = () => {
  const [startDate, setStartDate] = useState("");
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [days, setDays] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const nextId = useRef(0);

  const newId = (day) => {
    nextId.current += 1;
    return `${day}-new-${nextId.current}`;
  };

  const load = (schedule) => {
    const next = {
      startDate: schedule?.startDate ?? "",
      duration: String(schedule?.duration || DEFAULT_DURATION),
      days: schedule?.days ?? [],
    };
    setStartDate(next.startDate);
    setDuration(next.duration);
    setDays(next.days);
    setBaseline(next);
  };

  const addDay = (day) => setDays((previous) => [...previous, { id: newId(day), day, time: null }]);

  const removeDay = (id) => setDays((previous) => previous?.filter((entry) => entry?.id !== id));

  const changeTime = (id, time) =>
    setDays((previous) => previous?.map((entry) => (entry?.id === id ? { ...entry, time } : entry)));

  const isChanged = !!baseline && signature({ startDate, duration, days }) !== signature(baseline);
  /* Two classes that run into each other are refused by the preview and the mentor
     list alike (`timings-overlap`), so such a schedule is not one to ask about; the
     page's blocker says what to fix. */
  const isComplete =
    !!startDate &&
    !!duration &&
    days?.length > 0 &&
    days?.every((entry) => !!entry?.time) &&
    !hasOverlappingClass(days, duration);
  /* The backend refuses a start that is not one of the batch days
     (`start-date-not-class-day`, R-04-05), and so do the preview and the mentor
     list. The draft knows both halves, so it answers it here rather than each
     screen asking the same question of the same two values. */
  const isStartOnClassDay = isClassDay(startDate, days);

  return {
    state: { startDate, duration, days },
    derived: { isChanged, isComplete, isStartOnClassDay },
    handlers: {
      onStartDateChange: setStartDate,
      onDurationChange: setDuration,
      onAddDay: addDay,
      onRemoveDay: removeDay,
      onTimeChange: changeTime,
    },
    load,
  };
};

export default useScheduleDraft;
